import type { FilterGroup } from '@Core/components/dynamicFields/filter/types';
import { hasResourceAccess } from '@Data/utils/policyManifest';
import {
  useCreateSharedFilter,
  useDeleteSharedFilter,
  useSharedFilters as useSharedFiltersQuery,
  useUpdateSharedFilter,
  type SharedFilter
} from '@Pages/api/SharedFilterApi';
import { normalizePersistedResourceDataTableAdvancedFilter } from '@Pages/pageRuntime/resourceDataTable/resourceDataTableFilterUtils';
import type { ResourceSortDescriptor, SdkResource } from '@Pages/pageRuntime/sdk/types';
import { useState } from 'react';

import { PolicyScope } from '@/types/enums';

const INDEX_SCOPE = Number(PolicyScope.Index);

/**
 * The live view state a shared filter hydrates: the pieces of a record view that get
 * saved together. `null` (or `''` for search) means "not configured", so consumers
 * fall back to their own defaults for that piece.
 */
export type SharedFilterViewState = {
  /** Filter conditions, in the records API's `$filter` shape. */
  filter: FilterGroup | null;
  /** Ordered sort, primary first, directly usable as a `useRecords` sort input. Empty = not configured. */
  sorts: ResourceSortDescriptor[];
  /** The primary sort (`sorts[0]`), or `null` when no sort is configured. */
  sort: ResourceSortDescriptor | null;
  /** Free-text search. Empty string = no search. */
  search: string;
  /** Page size, or `null` for the consumer's default. */
  perPage: number | null;
  /** Visible table column identifiers, including custom IDs, in display order; `null` uses defaults. */
  columns: string[] | null;
};

/** The editable view state. `sort` is derived from `sorts`, so it is not stored. */
export type StoredViewState = Omit<SharedFilterViewState, 'sort'>;

const EMPTY_VIEW_STATE: StoredViewState = {
  filter: null,
  sorts: [],
  search: '',
  perPage: null,
  columns: null
};

/**
 * Headless shared-filter state for a resource: the policy-gated list, the selection,
 * the live view state a selection hydrates, and the save/update/delete flows. Consumed
 * by `useResourceDataTable` for tables and exposed as `Sdk.hooks.useSharedFilters` for
 * everything else (lists, cards, custom views).
 *
 * Selecting a shared filter applies every configured piece of its view state; edits
 * through the setters never touch the shared filter itself — they only dirty the local
 * state until `reset` (discard), `create` (save the current state as a new private
 * filter), or `update` (overwrite a filter the viewer manages). `rename` changes a
 * filter's name (and, for sharing-capable viewers, its sharing scope) and never
 * touches its saved view state.
 *
 * Each shared filter carries a server-computed `can_manage` flag — `true` for the
 * viewer's own filters (and every filter for holders of the manage-shared-filters
 * permission) — which gates the update, rename, and remove affordances.
 */
export type SharedFiltersApi = SharedFilterViewState & {
  /** Views the viewer may use or manage, subject to resource policy visibility. */
  available: SharedFilter[];
  isLoading: boolean;
  selectedId: string | null;
  selected: SharedFilter | null;
  /** Applies the stored view state; `null` resets everything to defaults. */
  select: (sharedFilterId: string | null) => void;
  setFilter: (filter: FilterGroup | null) => void;
  /** Replaces the ordered sort list. An empty list clears the sort. */
  setSorts: (sorts: ResourceSortDescriptor[]) => void;
  /** Replaces the whole sort list with this one sort, or clears it when `null`. */
  setSort: (sort: ResourceSortDescriptor | null) => void;
  setSearch: (search: string) => void;
  setPerPage: (perPage: number | null) => void;
  setColumns: (columns: string[] | null) => void;
  /**
   * The view state has been edited away from the selected saved view, or from the
   * defaults when no saved view is selected. A selection that has not loaded yet is
   * never dirty.
   */
  isDirty: boolean;
  /** Re-applies the stored view state, discarding local edits. */
  reset: () => void;
  /** Whether the viewer may save the current view state as a new private view. */
  canCreate: boolean;
  /** Whether the viewer may choose a saved view's sharing scope (the Manage Shared Filters permission). */
  canShare: boolean;
  /**
   * Saves the current view state as a new saved view and selects it. Private by
   * default; `isPrivate: false` shares it with everyone (honored only when `canShare`).
   */
  create: (input: { name: string; isPrivate?: boolean }) => Promise<SharedFilter>;
  isCreating: boolean;
  /** Overwrites the selected view state with the current one. */
  update: () => Promise<SharedFilter>;
  isUpdating: boolean;
  /** Deletes a saved view the viewer manages; the selection falls back to defaults. */
  remove: (sharedFilterId: string) => Promise<void>;
  /**
   * Renames a saved view the viewer manages — and, when `canShare`, optionally
   * changes its sharing scope. Never sends the view state.
   */
  rename: (
    sharedFilterId: string,
    input: { name: string; isPrivate?: boolean }
  ) => Promise<SharedFilter>;
};

export type UseSharedFiltersOptions = {
  /** Set `false` to skip fetching (the view state and setters keep working). */
  enabled?: boolean;
};

/**
 * Internal options for runtime callers. `initialState` restores a selection and its
 * possibly edited view state, for example from a table's URL state. A selection that
 * is not in the loaded list clears as for a deleted view. `defaultSorts` is the sort
 * of the view state without a selection: at mount without `initialState`, and after
 * the selection clears through `select(null)`, `reset`, or `remove`.
 */
type UseSharedFiltersInternalOptions = UseSharedFiltersOptions & {
  initialState?: StoredViewState & { selectedId: string | null };
  defaultSorts?: readonly ResourceSortDescriptor[];
};

/** The saved view state a shared filter applies on selection, cloned for local editing. */
export function sharedFilterViewState(
  sharedFilter: SharedFilter | null | undefined
): StoredViewState {
  return {
    filter: normalizePersistedResourceDataTableAdvancedFilter(sharedFilter?.filter),
    sorts: sharedFilter?.sort?.map(({ key, direction }) => ({ key, direction })) ?? [],
    search: sharedFilter?.search ?? '',
    perPage: sharedFilter?.per_page ?? null,
    columns: sharedFilter?.columns ?? null
  };
}

/**
 * Order- and null-insensitive filter signature for the shared-filter dirty check. The
 * server echoes saved conditions with canonical key order and explicit nulls
 * (`case_insensitive: null`), which an equivalent chip-built group lacks.
 */
function normalizedFilterSignature(value: unknown): string {
  if (value == null) {
    return '';
  }

  return JSON.stringify(value, (_key, node: unknown) =>
    node !== null && typeof node === 'object' && !Array.isArray(node)
      ? Object.fromEntries(
          Object.entries(node)
            .filter(([, entry]) => entry != null)
            .toSorted(([a], [b]) => a.localeCompare(b))
        )
      : node
  );
}

/** Canonical signature covering every view-state piece, for the dirty check. */
function viewStateSignature(state: StoredViewState): string {
  return JSON.stringify({
    filter: normalizedFilterSignature(state.filter),
    sorts: state.sorts.map(({ key, direction }) => `${key}:${direction}`),
    search: state.search,
    perPage: state.perPage,
    columns: state.columns
  });
}

/** The current view state as a create/update payload (`''` search saves as null). */
function viewStatePayload(state: StoredViewState) {
  return {
    filter: state.filter,
    sort: state.sorts.length === 0 ? null : state.sorts,
    columns: state.columns,
    search: state.search === '' ? null : state.search,
    per_page: state.perPage
  };
}

/**
 * Shared filters for a resource, usable with or without a data table. Returns the
 * viewer's policy-gated filter list plus selection, live view state, and persistence.
 * Feed the view state straight into `useRecords` — every piece is already in its
 * query vocabulary:
 *
 * Exposed via `Sdk.hooks.useSharedFilters(resource, options)`.
 *
 * @example
 * const students = Sdk.resource('students');
 * const sharedFilters = Sdk.hooks.useSharedFilters(students);
 * const records = Sdk.hooks.useRecords(students, {
 *   filter: sharedFilters.filter,
 *   sort: sharedFilters.sorts,
 *   search: sharedFilters.search,
 *   perPage: sharedFilters.perPage
 * });
 *
 * <Sdk.Components.SharedFilters sharedFilters={sharedFilters} />
 */
export function useSharedFilters(
  resource: Pick<SdkResource, 'id' | 'policy_scope'>,
  options?: UseSharedFiltersInternalOptions
): SharedFiltersApi {
  const enabled = (options?.enabled ?? true) && hasResourceAccess(resource, INDEX_SCOPE);

  const { data: sharedFiltersResponse, isLoading } = useSharedFiltersQuery({
    resourceId: resource.id,
    enabled
  });
  const available = enabled ? (sharedFiltersResponse?.data ?? []) : [];
  const createSharedFilterMutation = useCreateSharedFilter(resource.id ?? null);
  const updateSharedFilterMutation = useUpdateSharedFilter(resource.id ?? null);
  const deleteSharedFilterMutation = useDeleteSharedFilter(resource.id ?? null);

  const unselectedViewState = (): StoredViewState => ({
    ...EMPTY_VIEW_STATE,
    sorts: options?.defaultSorts?.map(({ key, direction }) => ({ key, direction })) ?? []
  });

  const [viewState, setViewState] = useState<StoredViewState>(() =>
    options?.initialState === undefined
      ? unselectedViewState()
      : {
          filter: options.initialState.filter,
          sorts: options.initialState.sorts,
          search: options.initialState.search,
          perPage: options.initialState.perPage,
          columns: options.initialState.columns
        }
  );
  const [selectedId, setSelectedId] = useState<string | null>(
    () => options?.initialState?.selectedId ?? null
  );

  // A deleted or unshared filter drops out of the list. Its hydrated view state stays —
  // it is the viewer's working state now — but the stale selection clears.
  if (
    selectedId !== null &&
    sharedFiltersResponse !== undefined &&
    !available.some((sharedFilter) => sharedFilter.id === selectedId)
  ) {
    setSelectedId(null);
  }

  const select = (sharedFilterId: string | null) => {
    setSelectedId(sharedFilterId);
    setViewState(
      sharedFilterId === null
        ? unselectedViewState()
        : sharedFilterViewState(
            available.find((sharedFilter) => sharedFilter.id === sharedFilterId)
          )
    );
  };

  const selected = available.find((sharedFilter) => sharedFilter.id === selectedId) ?? null;
  const baselineViewState =
    selectedId === null ? unselectedViewState() : selected && sharedFilterViewState(selected);
  const isDirty =
    baselineViewState !== null &&
    viewStateSignature(viewState) !== viewStateSignature(baselineViewState);

  const reset = () => {
    setViewState(selected === null ? unselectedViewState() : sharedFilterViewState(selected));
  };

  const setFilter = (filter: FilterGroup | null) => {
    setViewState((previous) => ({ ...previous, filter }));
  };
  const setSorts = (sorts: ResourceSortDescriptor[]) => {
    setViewState((previous) => ({ ...previous, sorts }));
  };
  const setSort = (sort: ResourceSortDescriptor | null) => {
    setSorts(sort === null ? [] : [sort]);
  };
  const setSearch = (search: string) => {
    setViewState((previous) => ({ ...previous, search }));
  };
  const setPerPage = (perPage: number | null) => {
    setViewState((previous) => ({ ...previous, perPage }));
  };
  const setColumns = (columns: string[] | null) => {
    setViewState((previous) => ({ ...previous, columns }));
  };

  const createSharedFilterMutate = createSharedFilterMutation.mutateAsync;
  const create = async ({ name, isPrivate }: { name: string; isPrivate?: boolean }) => {
    const created = await createSharedFilterMutate({
      name,
      is_private: isPrivate ?? true,
      ...viewStatePayload(viewState)
    });
    setSelectedId(created.id);
    return created;
  };

  const updateSharedFilterMutate = updateSharedFilterMutation.mutateAsync;
  const update = async () => {
    if (selectedId === null) {
      throw new Error('No shared filter is selected.');
    }
    return await updateSharedFilterMutate({
      sharedFilterId: selectedId,
      ...viewStatePayload(viewState)
    });
  };

  const rename = async (
    sharedFilterId: string,
    { name, isPrivate }: { name: string; isPrivate?: boolean }
  ) => {
    return await updateSharedFilterMutate({
      sharedFilterId,
      name,
      ...(isPrivate === undefined ? {} : { is_private: isPrivate })
    });
  };

  const deleteSharedFilterMutate = deleteSharedFilterMutation.mutateAsync;
  const remove = async (sharedFilterId: string) => {
    await deleteSharedFilterMutate(sharedFilterId);
    // Removing the applied filter returns the view to its defaults; other
    // deletions leave the current view state alone.
    if (sharedFilterId === selectedId) {
      setSelectedId(null);
      setViewState(unselectedViewState());
    }
  };

  return {
    ...viewState,
    sort: viewState.sorts[0] ?? null,
    available,
    isLoading: enabled && isLoading,
    selectedId,
    selected,
    select,
    setFilter,
    setSorts,
    setSort,
    setSearch,
    setPerPage,
    setColumns,
    isDirty,
    reset,
    canCreate: (enabled && sharedFiltersResponse?.can_create) ?? false,
    canShare: (enabled && sharedFiltersResponse?.can_share) ?? false,
    create,
    isCreating: createSharedFilterMutation.isPending,
    update,
    isUpdating: updateSharedFilterMutation.isPending,
    remove,
    rename
  };
}
